'use client';

/**
 * Eigene, gefüllte Announcement-Glyphen (kein generisches lucide-Outline) —
 * themebar via `currentColor`, gestochen scharf, RTL-neutral. Mit dezenter
 * Entrance-„Pop"-Animation + (nur beim Angebot) leisem Funkel-Shimmer. Alles
 * `prefers-reduced-motion`-aware.
 */

import { motion, useReducedMotion } from 'framer-motion';
import type { AnnouncementStyle } from '@/lib/storefront/announcement';

function Glyph({ style }: { style: AnnouncementStyle }) {
  if (style === 'offer') {
    // Funkeln / Spark — „besonderes Angebot". Zwei Sterne, der kleine shimmert.
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-full w-full" aria-hidden="true">
        <path d="M11 3c.45 4.1 2.9 6.55 7 7-4.1.45-6.55 2.9-7 7-.45-4.1-2.9-6.55-7-7 4.1-.45 6.55-2.9 7-7Z" />
        <path
          className="origin-center animate-pulse motion-reduce:animate-none"
          d="M18.5 13.5c.2 1.55 1.05 2.4 2.6 2.6-1.55.2-2.4 1.05-2.6 2.6-.2-1.55-1.05-2.4-2.6-2.6 1.55-.2 2.4-1.05 2.6-2.6Z"
        />
      </svg>
    );
  }
  if (style === 'important') {
    // Solides Ausrufezeichen — klar „wichtig", ohne Alarm-Warndreieck.
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" className="h-full w-full" aria-hidden="true">
        <rect x="10.3" y="4" width="3.4" height="10.6" rx="1.7" />
        <circle cx="12" cy="18.4" r="1.9" />
      </svg>
    );
  }
  // info — solides „i" im Kreis, weich.
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-full w-full" aria-hidden="true">
      <path
        d="M12 2.5A9.5 9.5 0 1 0 12 21.5 9.5 9.5 0 0 0 12 2.5Zm0 4.2a1.55 1.55 0 1 1 0 3.1 1.55 1.55 0 0 1 0-3.1Zm1.7 10.3h-3.4a1 1 0 0 1 0-2h.7v-2.6h-.5a1 1 0 1 1 0-2h1.5a1 1 0 0 1 1 1v3.6h.7a1 1 0 0 1 0 2Z"
      />
    </svg>
  );
}

export function AnnouncementIcon({
  style,
  className = '',
}: {
  style: AnnouncementStyle;
  className?: string;
}) {
  const reduce = useReducedMotion();
  return (
    <motion.span
      className={`inline-flex items-center justify-center ${className}`}
      initial={reduce ? false : { scale: 0.5, opacity: 0 }}
      animate={reduce ? undefined : { scale: 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 420, damping: 22, delay: 0.08 }}
    >
      <span className="h-4 w-4">
        <Glyph style={style} />
      </span>
    </motion.span>
  );
}
